// src/components/user/FollowButton.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { UserContext } from '../../context/userContext';
import { FaUserPlus, FaCheck, FaSpinner } from 'react-icons/fa';

const FollowButton = ({ userId, initialStatus = null, onFollowChange }) => {
  // Possible states: null, 'pending', 'accepted'
  const [followStatus, setFollowStatus] = useState(initialStatus);
  const [isLoading, setIsLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  const { currentUser } = useContext(UserContext);
  const navigate = useNavigate();

  // Check follow status on mount if not provided
  useEffect(() => {
    if (currentUser && userId !== currentUser.id && initialStatus === null) {
      checkFollowStatus();
    }
  }, [currentUser, userId, initialStatus]);

  // Check if current user follows this user
  const checkFollowStatus = async () => {
    if (!currentUser) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/followers/check/${userId}`,
        {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        }
      );
      
      if (response.data.following) {
        setFollowStatus(response.data.status);
      } else {
        setFollowStatus(null);
      }
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  };

  // Follow user
  const followUser = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/followers`,
        { userId },
        {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        }
      );
      
      setFollowStatus(response.data.status);
      
      if (onFollowChange) {
        onFollowChange(true, response.data.status);
      }
    } catch (error) {
      console.error('Error following user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Unfollow user
  const unfollowUser = async () => {
    if (!currentUser) return;
    
    setIsLoading(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/followers/${userId}`,
        {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        }
      );
      
      setFollowStatus(null);
      
      if (onFollowChange) {
        onFollowChange(false, null);
      }
    } catch (error) {
      console.error('Error unfollowing user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel follow request
  const cancelRequest = async () => {
    await unfollowUser();
  };

  // Render appropriate button based on follow status
  if (!currentUser || userId === currentUser.id) {
    return null; // Don't show button on own profile
  }

  if (isLoading) {
    return (
      <button 
        className="inline-flex items-center px-4 py-2 bg-gray-200 text-gray-700 rounded-md transition-colors"
        disabled
      >
        <FaSpinner className="animate-spin mr-2" size={14} />
        Loading
      </button>
    );
  }

  if (followStatus === 'accepted') {
    return (
      <button 
        className={`inline-flex items-center px-4 py-2 rounded-md transition-colors ${
          isHovered 
            ? 'bg-red-50 text-red-600 border border-red-200' 
            : 'bg-gray-200 text-gray-800 border border-gray-200'
        }`}
        onClick={unfollowUser}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {isHovered ? 'Unfollow' : (
          <>
            <FaCheck className="mr-2" size={14} />
            Following
          </>
        )}
      </button>
    );
  }

  if (followStatus === 'pending') {
    return (
      <button 
        className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
        onClick={cancelRequest}
      >
        Requested
      </button>
    );
  }

  return (
    <button 
      className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition-colors"
      onClick={followUser}
    >
      <FaUserPlus className="mr-2" size={14} />
      Follow
    </button>
  );
};

export default FollowButton;